import { ArrowDown, ArrowUp, Info, LucideIcon, SearchX } from "lucide-react";
import { ReactNode, Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";

import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/v2/table";
import { TablePaginator } from "@/components/ui/v2/table/table-paginator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/v2/tooltip";
import { cn } from "@/lib/utils/cn";
import {
  SortDirection,
  TableColumn,
  TableProps,
} from "@/types/table/tableProps";

export interface DataTableProps<T> extends TableProps<T> {
  // Override TableProps handlers
  onSort?: (
    accessor: TableColumn<T>["accessor"],
    direction: SortDirection | null
  ) => void;
  onRowClick?: (row: T) => void;
  // Extended props for pagination
  page?: number;
  pageSize?: number;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  emptyMessage?: string;
  emptyState?: {
    icon?: LucideIcon;
    title?: string;
    description?: string;
    action?: {
      label: string;
      onClick?: () => void;
      href?: string;
      icon?: ReactNode;
    };
  };
  getRowId?: (row: T, index: number) => string | number;
  getRowRef?: (row: T, index: number) => Ref<HTMLTableRowElement> | undefined;
  /** CSS class applied to the `<table>` element (e.g. "table-fixed" for equal column widths). */
  tableClassName?: string;
  // Current sort state
  orderBy?: string | null;
  descending?: boolean;
}

export function DataTable<T>({
  columns,
  data = [],
  isLoading = false,
  onRowClick,
  onSort,
  defaultSortState,
  // Extended props
  page = 1,
  pageSize = 25,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
  emptyMessage,
  emptyState,
  getRowId,
  getRowRef,
  tableClassName,
  // Current sort state
  orderBy,
  descending = false,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  // Derive sort state from defaultSortState or props
  const sortState = useMemo(() => {
    if (defaultSortState) {
      const accessor =
        typeof defaultSortState.accessor === "string"
          ? defaultSortState.accessor
          : null;
      const descending = defaultSortState.direction === SortDirection.DESC;
      return { orderBy: accessor, descending };
    }
    return {
      orderBy: orderBy ?? null,
      descending: descending ?? false,
    };
  }, [defaultSortState, orderBy, descending]);

  const { orderBy: currentOrderBy, descending: currentDescending } = sortState;

  const handleColumnSort = (accessor: string | undefined) => {
    if (!onSort || !accessor) return;

    // Determine next sort state: null → DESC → ASC → null (3 states)
    let nextDirection: SortDirection | null;

    if (currentOrderBy !== accessor) {
      // New column - start with descending
      nextDirection = SortDirection.DESC;
    } else if (currentDescending) {
      // Same column, currently descending → switch to ascending
      nextDirection = SortDirection.ASC;
    } else {
      // Same column, currently ascending → reset to null (no sort)
      nextDirection = null;
    }

    onSort(accessor, nextDirection);
  };

  const defaultGetRowId = (row: T, index: number): string | number => {
    if (getRowId) {
      return getRowId(row, index);
    }
    // Try to get id from common property names
    const rowRecord = row as Record<string, unknown>;
    return (rowRecord?.id2 as string) ?? (rowRecord?.id as number);
  };

  return (
    <>
      <Table className={tableClassName}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.map((column, colIndex) => {
              const accessorKey = column.accessor as string;
              const finalKey = accessorKey || `col-${colIndex}`;
              const isSortable =
                column.accessor &&
                typeof column.accessor === "string" &&
                column.sortable !== false;
              const isActiveSort = currentOrderBy === column.accessor;

              return (
                <TableHead
                  key={finalKey}
                  className={cn(
                    column.textAlign === "right"
                      ? "text-right"
                      : column.textAlign === "center"
                        ? "text-center"
                        : "text-left",
                    isSortable ? "cursor-pointer" : "cursor-default"
                  )}
                  style={column.width ? { width: column.width } : { width: `${100 / columns.length}%` }}
                  onClick={() => {
                    if (isSortable && column.accessor) {
                      handleColumnSort(column.accessor as string);
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex items-center gap-1",
                      column.textAlign === "right"
                        ? "justify-end w-full"
                        : column.textAlign === "center"
                          ? "justify-center w-full"
                          : "justify-start"
                    )}
                  >
                    {column.header}
                    {column.headerTooltip && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span onClick={(e) => e.stopPropagation()} className="shrink-0 cursor-help">
                              <Info className="h-3.5 w-3.5 text-muted-foreground/60" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[260px]">
                            <p>{column.headerTooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {isActiveSort && (
                      <span className="ml-1">
                        {currentDescending ? (
                          <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUp className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: Math.min(pageSize, 10) }).map(
              (_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {columns.map((column, colIndex) => {
                    const accessorKey = column.accessor as string;
                    const finalKey = accessorKey || `col-${colIndex}`;
                    return (
                      <TableCell
                        key={`skeleton-${finalKey}-${rowIndex}`}
                        className={cn(
                          column.textAlign === "right"
                            ? "text-right"
                            : column.textAlign === "center"
                              ? "text-center"
                              : "text-left"
                        )}
                      >
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              )
            )
          ) : data.length > 0 ? (
            data.map((row, index) => {
              const rowId = defaultGetRowId(row, index);
              return (
                <TableRow
                  key={rowId}
                  ref={getRowRef?.(row, index)}
                  onClick={() => onRowClick?.(row)}
                  className={onRowClick ? "cursor-pointer" : undefined}
                >
                  {columns.map((column, colIndex) => {
                    const accessorKey = column.accessor as string;
                    const finalKey = accessorKey || `col-${colIndex}`;
                    return (
                      <TableCell
                        key={`${finalKey}-${rowId}`}
                        className={cn(
                          column.textAlign === "right"
                            ? "text-right"
                            : column.textAlign === "center"
                              ? "text-center"
                              : "text-left"
                        )}
                      >
                        {column.Cell ? (
                          column.Cell(row, index)
                        ) : (
                          <span>
                            {String(
                              (row as Record<string, unknown>)[
                                column.accessor as string
                              ] ?? ""
                            )}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="p-0">
                <EmptyState
                  icon={emptyState?.icon ?? SearchX}
                  title={
                    emptyState?.title ?? emptyMessage ?? t("common.noData")
                  }
                  description={emptyState?.description}
                  action={emptyState?.action}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {onPageChange && (
        <div className="mt-4">
          <TablePaginator
            isLoading={isLoading}
            itemsPerPage={pageSize}
            page={page}
            setCurrentPage={onPageChange}
            setItemsPerPage={onPageSizeChange}
            totalItems={totalRecords}
          />
        </div>
      )}
    </>
  );
}
